import React, { useState, useCallback } from 'react';
import { useTranslation } from "react-i18next";
import { format, parseISO, parse } from 'date-fns';
import { useChartStyles } from './ChartStyles';
import useChartFormatting from '../../hooks/useChartFormatting';
import { alertDecisionLabel, isAlertCancelled } from 'helpers/conditionMonitoringAlert';

// Helper function to truncate text and add ellipsis
const truncateText = (text, maxLength = 20) => {
  if (!text || typeof text !== 'string') return text;
  return text.length > maxLength ? `${text.substring(0, maxLength)}...` : text;
};

// Formats a date label based on time grouping
export const formatTooltipDate = (label, timeGrouping = 'Monthly') => {
  let formattedDate = label;
  
  try {
    if (label?.includes('-')) {
      const date = parseISO(label);
      switch (timeGrouping) {
        case 'Hourly':
          formattedDate = format(date, 'HH:mm, MMMM d, yyyy');
          break;
        case 'Daily':
          formattedDate = format(date, 'MMMM d, yyyy');
          break;
        case 'Weekly':
          formattedDate = format(date, "'Week of' MMMM d, yyyy");
          break;
        case 'Monthly':
          formattedDate = format(date, 'MMMM yyyy');
          break;
        default:
          formattedDate = label;
      }
    } else if (label?.includes('/')) {
      const date = parse(label, 'M/yyyy', new Date());
      formattedDate = format(date, 'MMMM yyyy');
    }
  } catch (e) {
    // If parsing fails, use the original label
    formattedDate = label;
  }
  
  return formattedDate;
};

// Helper to determine shape based on chart type and item type
const getShapeStyle = (item, chartType, color) => {
  // For multi-bar-line chart, use the item type
  if (chartType === 'multi-bar-line' && item?.type === 'line') {
    return {
      borderRadius: '50%', // Circle for lines
      background: color,
    };
  }
  
  // Chart type specific shapes
  switch (chartType) {
    case 'line':
    case 'area':
      return {
        borderRadius: '50%', // Circle for lines/areas
        background: color,
      };
    case 'pie':
      return {
        borderRadius: '50%', // Circle for pie slices
        background: color,
      };
    case 'treemap':
      return {
        borderRadius: '2px', // Square for treemap
        background: color,
      };
    default:
      // Default for bar charts and others
      if (chartType?.includes('line')) {
        return {
          borderRadius: '50%', // Circle for any line type
          background: color,
        };
      }
      return {
        borderRadius: '2px', // Square for others
        background: color,
      };
  }
};

// Shared chart tooltip component
export const ChartTooltip = ({
  active,
  payload,
  legendItems,
  showToolTip,
  label,
  timeGrouping,
  chartType = 'line',
  transformedData,
  useSimplifiedTooltip = false,
  allowCMEdit = false,
  tooltipCaptions = {}
}) => {
  const { classes } = useChartStyles();
  const { t } = useTranslation();

  if ((!active && !showToolTip) || !payload || !payload.length) return null;
  
  // Get the first payload item which is the one being hovered
  const currentPayload = payload[0];
  const data = currentPayload.payload;
  
  // Use displayDate if available, otherwise use formatted label
  const formattedLabel = data.displayDate || (label ? formatTooltipDate(label, timeGrouping) : (data.name || data.label));
  
  // Check if there are defects to display
  const hasDefects = data.defects && data.defects.length > 0;
  const defects = hasDefects ? data.defects : [];
  
  // For reports with siteAssetName
  const currentAssetName = data.siteAssetName || '';
  
  // Create a single-item payload array containing only the hovered item
  const filteredPayload = [currentPayload];
  
  return (
    <div className={classes.customTooltip}>
      <div className={classes.tooltipHeader}>
        {truncateText(formattedLabel, 40)}
      </div>
      
      {data.siteAssetName && (
        <div className={classes.tooltipLabel} style={{ marginBottom: '6px' }}>
          <span>{t("label.entityColon", { entity: tooltipCaptions.asset || t("entity.asset") })} </span>
          <strong>{truncateText(data.siteAssetName, 30)}</strong>
        </div>
      )}
      
      {/* Use simplified tooltip if specified */}
      {useSimplifiedTooltip ? (
        <>
          <div className={classes.tooltipLabel}>
            <span>{t("label.response")}: </span>
            <strong>{data.valueNumeric || data.value}</strong>
          </div>
          {data.taskQuestionThumbnailURL && (
            <div style={{ marginTop: '8px' }}>
              <img
                src={data.taskQuestionThumbnailURL}
                alt="Question thumbnail"
                style={{
                  display: 'block',
                  maxWidth: '180px',
                  maxHeight: '140px',
                  width: '100%',
                  height: 'auto',
                  objectFit: 'contain'
                }}
              />
            </div>
          )}
          {data.serviceWorkOrder && (
            <div className={classes.tooltipLabel}>
              <span>{tooltipCaptions.serviceWorkOrder || 'Service Work Order'}: </span>
              <strong>{data.serviceWorkOrder}</strong>
            </div>
          )}
          {data.modifiedByUserName && (
            <div className={classes.tooltipLabel}>
              <span>{tooltipCaptions.user || 'User'}: </span>
              <strong>{truncateText(data.modifiedByUserName, 30)}</strong>
            </div>
          )}
          {/* Show edit info if this point has been edited */}
          {data.isEdited && (
            <div style={{
              marginTop: '8px',
              paddingTop: '8px',
              borderTop: '1px solid #FFE0B2',
              backgroundColor: '#FFF8E1',
              margin: '8px -8px -8px -8px',
              padding: '8px'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong style={{ fontSize: '12px', color: '#E65100' }}>{t("label.edited")}</strong>
              </div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                <div>{t("label.by")}: {data.modifiedByUserName}</div>
                <div>{t("label.onLabel")}: {data.modifiedDateTime}</div>
                <div>{t("label.reason")}: {data.editReasonName}{data.editOtherReason ? ` - ${truncateText(data.editOtherReason, 30)}` : ''}</div>
              </div>
            </div>
          )}
          {/* Show CM alert decision if this reading fired an alert */}
          {data.alertTriggered && (
            <div style={{
              marginTop: '8px',
              borderTop: isAlertCancelled(data.alertDecision) ? '1px solid #BDBDBD' : '1px solid #F5C77E',
              backgroundColor: isAlertCancelled(data.alertDecision) ? '#F5F5F5' : '#FFF8E1',
              margin: '8px -8px -8px -8px',
              padding: '8px'
            }}>
              <div style={{ marginBottom: '4px' }}>
                <strong style={{
                  fontSize: '12px',
                  color: isAlertCancelled(data.alertDecision) ? '#616161' : '#C77C00'
                }}>
                  {t("label.alertWithDecision", { decision: alertDecisionLabel(data.alertDecision) })}
                </strong>
              </div>
              <div style={{ fontSize: '11px', color: '#666' }}>
                {data.modifiedByUserName ? <div>{t("label.by")}: {data.modifiedByUserName}</div> : null}
                {data.modifiedDateTime ? <div>{t("label.onLabel")}: {data.modifiedDateTime}</div> : null}
              </div>
            </div>
          )}
          {/* Click to edit hint */}
          {allowCMEdit && (
            <div style={{
              marginTop: '8px',
              fontSize: '11px',
              color: '#666',
              textAlign: 'center'
            }}>
              {t("message.clickDataPointToEdit")}
            </div>
          )}
        </>
      ) : (
        <div>
          {payload.map((entry) => {
            // Skip entries with no value
            if (!entry.value && entry.value !== 0) return null;
            
            const item = legendItems?.find(i => i.key === entry.dataKey);
            
            // --- Find original index ---
            let originalIndex = -1;
            if (transformedData && entry.payload) {
              // Assuming entry.payload is the original data object reference
              originalIndex = transformedData.findIndex(dataPoint => dataPoint === entry.payload);
            }
            // ------------------------

            // Calculate color using original index if found, otherwise fallback
            let color = item?.defaultColor; // Start with default
            if (item?.getColorByIndex && originalIndex !== -1) {
               color = item.getColorByIndex(originalIndex); // Use original index
            } else {
               color = color || entry.color || entry.fill;
            }
            
            // Get shape style based on chart type and item type
            const shapeStyle = getShapeStyle(item, chartType, color);
            
            return (
              <div key={`tooltip-item-${entry.dataKey}-${item?.key || entry.name || Math.random()}`} className={classes.tooltipLabel}>
                <div 
                  className={classes.colorBox} 
                  style={shapeStyle}
                />
                <div>
                  <span>{truncateText(item?.label || entry.name, 30)}: </span>
                  <strong>{entry.value}</strong>
                  {data.percentage && <span> ({data.percentage}%)</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
      
      {/* Display cancelled defect indicator */}
      {data.isDefectCancelled && !hasDefects && (
        <div style={{
          marginTop: '8px',
          borderTop: '1px solid #ccc',
          paddingTop: '8px',
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
          fontSize: '12px',
          color: '#E65100'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            backgroundColor: '#FF6D00',
            flexShrink: 0
          }} />
          <span>{t("message.defectTriggeredButCancelled", { entity: tooltipCaptions.defect || 'Defect', user: data.modifiedByUserName || 'User' })}</span>
        </div>
      )}

      {/* Display defects if present */}
      {hasDefects && (
        <div style={{ marginTop: '8px', borderTop: '1px solid #ccc', paddingTop: '8px' }}>
          <b style={{ display: 'block', marginBottom: '6px' }}>{t("label.entityColon", { entity: tooltipCaptions.defectPlural || t("entity.defect", { count: 2 }) })}</b>
          {defects.map((defect, index) => (
            <div key={index} style={{ marginBottom: '4px', fontSize: '13px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start' }}>
                <div style={{ 
                  width: '8px', 
                  height: '8px', 
                  borderRadius: '50%', 
                  backgroundColor: '#FF0000',
                  marginTop: '5px',
                  marginRight: '6px'
                }}/>
                <div>
                  <div><b>#{defect.number}</b> - {truncateText(defect.defectDetail, 40)}</div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {truncateText(defect.defectRiskRatingName, 15)} • {truncateText(defect.defectTypeName, 15)} • {truncateText(defect.defectStatusName, 15)}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Shared tooltip state handlers
export const useTooltipHandlers = () => {
  const [tooltipActive, setTooltipActive] = useState(false);
  
  const handleMouseOver = useCallback(() => setTooltipActive(true), []);
  const handleMouseLeave = useCallback(() => setTooltipActive(false), []);
  
  return {
    tooltipActive,
    handleMouseOver,
    handleMouseLeave
  };
}; 
